import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Image,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useState, useMemo } from 'react';
import ModalComponent from '../Modal';
import ServerSideDatatable from '../ServerSideDatatable';
import DeleteBlueIcon from '../../assets/images/delete-blue-icon.png';
import AlertModalOverlay from '../AlertModalComponent';
import FinancialRuleList from '../FinancialRules';
import StateSelectionModal from './StateSelectionModal';
import { getClientStates, deleteStateFinancialRules } from '../../redux/action/client.action';
import { CUSTOM_TOAST_DURATION, CUSTOM_TOAST_DURATION_NULL, TOAST_SETTINGS } from '../../constants';

const StateFinRulesModal = ({ isOpen, onClose, selectedClient }) => {
  const toast = useToast();
  const [isStateListExists, setIsStateListExists] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [isLoadingStates, setIsLoadingStates] = useState(false);

  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [isFinancialRuleModalOpen, setIsFinancialRuleModalOpen] = useState(false);
  const [deletingBulkRuleId, setBulkDeletingRuleId] = useState(null);
  const [isDeleteOperationLoading, setIsDeleteOperationLoading] = useState(false);
  const [isStateSelectionModalOpen, setIsStateSelectionModalOpen] = useState(false);
  const [hasRulesChanged, setHasRulesChanged] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(false);

  // Pagination handler for ServerSideDatatable
  const handleClientStatesPagination = async (limit, pageNumber) => {
    try {
      setIsLoadingStates(true);
      const result = await getClientStates(selectedClient?.client_id, limit, pageNumber);

      if (result.ok && result.data) {
        setIsStateListExists(result.data.tax_regions.length > 0);
        return {
          data: {
            tax_regions: result.data.tax_regions,
            count: result.data.pagination.total_records || 0,
          },
        };
      }
      return {
        data: {
          tax_regions: [],
          count: 0,
        },
      };
    } catch (error) {
      console.error('Error fetching paginated client states:', error);
      return {
        data: {
          tax_regions: [],
          count: 0,
        },
      };
    } finally {
      setIsLoadingStates(false);
    }
  };

  // Handle delete rule
  const handleBulkDeleteRule = (ruleId) => {
    setBulkDeletingRuleId(ruleId);
    setIsBulkDeleteModalOpen(true);
  };

  const handleModalClose = () => {
    onClose();
  };

  const confirmBulkDeleteRule = async () => {
    try {
      if (deletingBulkRuleId) {
        setIsBulkDeleteModalOpen(false);
        setIsDeleteOperationLoading(true);
        const result = await deleteStateFinancialRules(
          selectedClient?.client_id,
          deletingBulkRuleId
        );

        if (result.ok) {
          toast({
            ...TOAST_SETTINGS.SETTINGS,
            duration: CUSTOM_TOAST_DURATION.duration,
            status: TOAST_SETTINGS.SUCCESS,
            title: result.message || 'Financial rules deleted successfully',
          });
          // Refresh the states list to update rules count by Triggering refresh in ServerSideDatatable
          setRefreshTrigger((prev) => !prev);
        } else {
          toast({
            ...TOAST_SETTINGS.SETTINGS,
            duration: CUSTOM_TOAST_DURATION_NULL.duration,
            status: TOAST_SETTINGS.ERROR,
            title: result.error || 'Failed to delete financial rules',
          });
        }
        setIsDeleteOperationLoading(false);
        setBulkDeletingRuleId(null);
      }
    } catch (error) {
      setIsDeleteOperationLoading(false);
      setBulkDeletingRuleId(null);
      console.error('Error deleting state financial rules:', error);
      toast({
        ...TOAST_SETTINGS.SETTINGS,
        duration: CUSTOM_TOAST_DURATION.duration,
        status: TOAST_SETTINGS.ERROR,
        title: 'Failed to delete financial rules',
      });
    }
  };

  const handleBulkDeleteClose = () => {
    setIsBulkDeleteModalOpen(false);
    setBulkDeletingRuleId(null);
  };

  const handleRulesView = (rule) => {
    // Open modal to view rule
    setSelectedState(rule);
    setIsFinancialRuleModalOpen(true);
  };
  const handleFinancialRuleModalClose = () => {
    setIsFinancialRuleModalOpen(false);
    // Call the fetch client states if rules changed by Triggering refresh in ServerSideDatatable
    if (hasRulesChanged) {
      setRefreshTrigger((prev) => !prev);
      setHasRulesChanged(false);
    }
    setSelectedState(null);
  };

  // Render function for action column
  const handleActionColumn = (id, rule) => {
    return (
      <HStack spacing={2}>
        <Box position="relative" display="inline-block">
          <Button
            size="sm"
            bg="main.semiPrimary"
            color="main.white"
            _hover={{ bg: 'main.primary' }}
            _active={{
              bg: 'main.semiPrimary',
              borderColor: 'main.primary',
            }}
            onClick={() => handleRulesView(rule)}
            isDisabled={selectedState?.id === id}
          >
            View Rule
          </Button>
          {selectedState?.id === id && (
            <Flex
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              justify="center"
              align="center"
              borderRadius="md"
              bg="rgba(255, 255, 255, 0.7)"
            >
              <Spinner size="sm" />
            </Flex>
          )}
        </Box>
        {rule.rules_count > 0 && (
          <Box position="relative" display="inline-block">
            <IconButton
              size="sm"
              aria-label="Delete rule"
              icon={<Image src={DeleteBlueIcon} height={4} />}
              variant="ghost"
              onClick={() => handleBulkDeleteRule(id)}
              isDisabled={isDeleteOperationLoading && deletingBulkRuleId === id}
            />
            {isDeleteOperationLoading && deletingBulkRuleId === id && (
              <Flex
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                justify="center"
                align="center"
                borderRadius="md"
                bg="rgba(255, 255, 255, 0.7)"
              >
                <Spinner size="sm" />
              </Flex>
            )}
          </Box>
        )}
      </HStack>
    );
  };

  const columns = useMemo(
    () => [
      {
        label: 'State',
        field: 'name',
        sort: false,
      },
      {
        label: 'Rules',
        field: 'rules_count',
        sort: false,
      },
      {
        label: 'Action',
        field: 'id',
        sort: false,
        render: handleActionColumn,
      },
    ],
    [deletingBulkRuleId, isDeleteOperationLoading]
  );

  return (
    <>
      <StateSelectionModal
        isOpen={isStateSelectionModalOpen}
        onClose={() => setIsStateSelectionModalOpen(false)}
        clientId={selectedClient?.client_id}
        onRefreshParent={() => {
          setIsStateSelectionModalOpen();
          // Trigger refresh in ServerSideDatatable
          setRefreshTrigger((prev) => !prev);
        }}
      />
      <ModalComponent
        specifySize="6xl"
        isOpen={isOpen}
        onClose={handleModalClose}
        modalTitle={`Manage State Financial Rules - ${selectedClient?.client_name}`}
        modalContent={
          <Box>
            <Button
              bg="main.semiPrimary"
              color="main.white"
              _hover={{ bg: 'main.primary' }}
              _active={{
                bg: 'main.semiPrimary',
                borderColor: 'main.primary',
              }}
              size="sm"
              onClick={() => setIsStateSelectionModalOpen(true)}
              isDisabled={isLoadingStates}
            >
              {isStateListExists ? 'Update State Selection' : 'State Selection'}
            </Button>
            <ServerSideDatatable
              pageSize={10}
              initialSortKey="name"
              onPaginate={handleClientStatesPagination}
              pagination
              dataKey="tax_regions"
              column={columns}
              pages={[5, 10, 15]}
              isLoading={isLoadingStates}
              refresh={refreshTrigger}
            />
          </Box>
        }
      />
      {/* Delete Confirmation Modal */}
      <AlertModalOverlay
        isOpen={isBulkDeleteModalOpen}
        onClose={handleBulkDeleteClose}
        title="Bulk Delete Financial Rule"
        body={
          <Text fontSize="md">
            Are you sure you want to delete all the rules of this state? This action cannot be
            undone.
          </Text>
        }
        negative={
          <Button
            onClick={handleBulkDeleteClose}
            bg="main.secondary"
            mr={3}
            _hover={{ bg: 'main.secondary' }}
            color="main.black"
          >
            Cancel
          </Button>
        }
        positive={
          <Button onClick={confirmBulkDeleteRule} colorScheme="red" ml={3}>
            Delete
          </Button>
        }
      />

      {selectedClient && selectedState && (
        <FinancialRuleList
          isOpen={isFinancialRuleModalOpen}
          onClose={handleFinancialRuleModalClose}
          clientId={selectedClient?.client_id}
          clientWeekDay={selectedClient?.weekday_start}
          selectedState={selectedState}
          setRulesChanged={setHasRulesChanged}
        />
      )}
    </>
  );
};

export default StateFinRulesModal;
